import { Button, Flex, Space, Typography } from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import {
  toNegativeCssBox,
  usePagePadding,
} from "components/layout/pagePadding";
import { useNavigation, useTranslate } from "@refinedev/core";
import { CSSProperties, ReactNode } from "react";
import { CaretLeftIcon } from "@phosphor-icons/react";
import styled from "styled-components";
import { useProjectNavigate } from "pages/media/projects/hooks/useProjectNavigate";

const SOURCES = {
  projects: "media_projects",
  libraries: "media_asset_libraries",
  media: "media",
  dashboard: "media_dashboard",
  assets: "assets",
  brandkits: "media_brandkits",
  contacts: "media_contacts",
  voices: "media_voices",
  faces: "media_faces",
  presenters: "media_presenters",
  ctas: "media_ctas",
  products: "media_products",
  documents: "media_documents",
  media_media: "media_media",
  batches: "media_batches",
  media_assets: "media_assets",
  assets_review: "media_assets_review",
  templates: "media_templates",
  pronunciations: "media_pronunciations",
  users: "users",
  organization: "organization",
  system: "system",
  vaults: "vaults",
} as const;

export const SubPageHeader = ({
  itemId,
  itemTitle,
  page,
  rightContent,
  leftContent,
  style,
  source,
  backButton = true,
  onBack,
  spaced = false,
}: {
  itemId?: string; //on create page there is no itemId yet
  itemTitle: string;
  page: string; // takes key from translation subPageHeader object
  rightContent?: ReactNode;
  leftContent?: ReactNode;
  style?: CSSProperties;
  source: keyof typeof SOURCES;
  backButton?: boolean;
  onBack?: () => void;
  spaced?: boolean;
}) => {
  const { theme } = useAntTheme();
  // Full-bleed header bar: cancels the app shell's page padding.
  const pagePadding = usePagePadding();
  const t = useTranslate();
  const { show, list } = useNavigation();
  const { params: projectParams, navigateToProject } = useProjectNavigate();

  const handleShowItem = () => {
    if (!itemId || !projectParams.projectId) return;
    if (source === "projects") {
      // better to use native browser back button to keep deep urls with storyboardIds
      navigateToProject({
        projectId: projectParams.projectId,
        storyboardId: projectParams.storyboardId,
        // not supported yet
        // instanceId: projectParams.instanceId,
      });
      return;
    }
    show(SOURCES[source], itemId);
  };

  const handleShowItemsList = () => {
    list(SOURCES[source]);
  };

  return (
    <Flex
      align="center"
      justify="space-between"
      style={{
        margin: toNegativeCssBox(pagePadding),
        padding: "8px 16px",
        marginBottom: spaced ? theme.paddingLG : 0,
        minHeight: 60, //save height when no backButton
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {leftContent}
        {backButton && (
          <Button
            type="text"
            size="large"
            icon={<CaretLeftIcon />}
            onClick={onBack ?? handleShowItem}
            style={{ fontSize: 18 }}
          >
            {t("buttons.back")}
          </Button>
        )}
      </div>

      <Space>
        <StyledText type="secondary" onClick={handleShowItemsList}>
          {t(`media.projects.subPageHeader.${source}`)} /
        </StyledText>
        <StyledText style={{ color: theme.colorText }} onClick={handleShowItem}>
          {itemTitle}
        </StyledText>
        <Typography.Text type="secondary" style={{ textWrap: "nowrap" }}>
          / {t(`media.projects.subPageHeader.${page}`)}
        </Typography.Text>
      </Space>
      <div>{rightContent}</div>
    </Flex>
  );
};

const StyledText = styled(Typography.Text)`
  cursor: pointer;
  text-wrap: nowrap;
`;
